import { useTranslation } from 'react-i18next';
import type { SegmentedProps } from 'antd';
import { Flex, Typography, Divider, Segmented } from 'antd';
import {
  IconTable,
  IconUpload,
  IconFileTypeCsv,
  IconHistory,
} from '@tabler/icons-react';
import type { TabContentViewProps } from './TabContent.types';
import { CsvMatchyUpload } from '~/app/components/knowledgeBase/csvUpload/csvUploadMatchyLibrary/CsvMatchyUpload';
import { CrudTableContainer } from '~/app/components/knowledgeBase/crudTable';
import { EventStateImportHistorySection } from '~/app/components/knowledgeBase/eventStateImportHistory';
import { tabToolbarStyle } from './TabContent.styles';

const { Text, Paragraph } = Typography;

export const TabContent = ({
  config,
  viewMode,
  onToggleView,
  lookupData,
  editingRows,
  onAddRow,
  onEditRow,
  onDeleteRow,
  onSaveRow,
  onCancelEdit,
  onRefresh,
  onApplySynonyms,
  search,
  onSearch,
  onMatchySubmit,
  associationCounts,
  associationLabel,
  associationCountsSecondary,
  associationLabelSecondary,
  onOpenTransfer,
  onOpenTransferSecondary,
  pagination,
  onPageChange,
}: TabContentViewProps) => {
  const { t } = useTranslation('componentSchema');
  const segmentedOptions: NonNullable<
    SegmentedProps<TabContentViewProps['viewMode']>['options']
  > = [
    {
      label: (
        <Flex align="center" gap={6}>
          <IconTable size={14} />
          <span>{t('tabContent.dataTable')}</span>
        </Flex>
      ),
      value: 'crud',
    },
    {
      label: (
        <Flex align="center" gap={6}>
          <IconUpload size={14} />
          <span>{t('tabContent.csvUpload')}</span>
        </Flex>
      ),
      value: 'upload',
    },
  ];

  if (config.name === 'Event State') {
    segmentedOptions.push({
      label: (
        <Flex align="center" gap={6}>
          <IconHistory size={14} />
          <span>{t('tabContent.importHistory')}</span>
        </Flex>
      ),
      value: 'history',
    });
  }

  return (
    <Flex vertical gap={16}>
      <Flex vertical gap={4}>
        <Flex align="center" gap={8}>
          <IconFileTypeCsv size={20} color="#1E6DF6" />
          <Text strong style={{ fontSize: 16 }}>
            {config.name}
          </Text>
        </Flex>
        <Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          {config.description}
        </Paragraph>
      </Flex>

      {!config.hideUpload && (
        <div style={tabToolbarStyle}>
          <Segmented
            value={viewMode}
            onChange={(value) =>
              onToggleView(value as TabContentViewProps['viewMode'])
            }
            options={segmentedOptions}
            style={{ borderRadius: 8 }}
          />
        </div>
      )}

      {!config.hideUpload && <Divider style={{ margin: 0 }} />}

      {viewMode === 'crud' || config.hideUpload ? (
        <CrudTableContainer
          config={config}
          data={lookupData.rows}
          loading={lookupData.loading}
          error={lookupData.error}
          editingRows={editingRows}
          onAddRow={onAddRow}
          onEditRow={onEditRow}
          onDeleteRow={onDeleteRow}
          onSaveRow={onSaveRow}
          onCancelEdit={onCancelEdit}
          onApplySynonyms={onApplySynonyms}
          onRefresh={onRefresh}
          pagination={pagination}
          onPageChange={(page, pageSize) =>
            onPageChange && onPageChange(page, pageSize)
          }
          search={search}
          onSearch={(q) => onSearch && onSearch(q)}
          associationCounts={associationCounts}
          associationLabel={associationLabel}
          onOpenTransfer={onOpenTransfer}
          associationCountsSecondary={associationCountsSecondary}
          associationLabelSecondary={associationLabelSecondary}
          onOpenTransferSecondary={onOpenTransferSecondary}
        />
      ) : viewMode === 'upload' ? (
        <CsvMatchyUpload
          config={config}
          onSubmitDataAction={(rows) => {
            onMatchySubmit(rows);
          }}
        />
      ) : config.name === 'Event State' ? (
        <EventStateImportHistorySection />
      ) : null}
    </Flex>
  );
};
